/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ICartViewProps, ICartData } from '@msdyn365-commerce-modules/cart';
import * as React from 'react';
import { ICartProps } from '../definition-extensions/cart.ext.props.autogenerated';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { Address, DeliveryOption, IContext } from '@msdyn365-commerce/retail-proxy';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { getProductPageUrlSync, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { parseString } from 'xml2js';
import { LogLevel } from '@msdyn365-commerce/retail-proxy/dist/interfaces/ITelemetry';
import { ICCartSpecificationData } from '../../../data/cart';
import { logDetails } from '../../../utils/debug-mode';


export type SelectedConfiguration = {
    Session: {
        Attribute: {
            $: {
                name: string;
                readableName: string;
                readableValue: string;
                value: string;
            };
        }[];
    };
};

const CartView: React.FC<ICartViewProps & ICartProps<ICartData & ICCartSpecificationData>> = props => {
    const {
        cartlines,
        data: { cart, countryRegions },
        config,
        resources
    } = props;
    logDetails(props.context.app.config.debugMode, props);

    const user = props.context.request.user;

    const getPrice = (price: number | undefined) => {
        if (typeof price !== 'undefined') {
            return props.context.cultureFormatter.formatCurrency(price);
        } else {
            return '';
        }
    };
    // const [inputValue, setInputValue] = React.useState('');
    // const handleInputChange = (e: any) => {
    //     setInputValue(e.target.value);
    // };
    // const handleApplyButtonClick = () => {
    //     // Check if inputValue is not empty before applying the coupon
    //     if (inputValue.trim() !== '') {
    //         // Add logic here to apply the coupon
    //         console.log('Applying coupon with value:', inputValue);
    //         // @ts-ignore
    //         console.log('promotions', cartlines);

    //         // You can further update the state or perform other actions as needed
    //         setInputValue('');
    //     } else {
    //         // Handle the case where the input is empty or contains only whitespace
    //         console.log('Please enter a valid coupon code.');
    //     }
    // };
    const handleRemoveCartLine = (cartLineId: string | undefined) => {
        if (!cartLineId) {
            return;
        }
        cart.result?.removeCartLines({
            cartLineIds: [cartLineId]
        });
    };
    // Add a new function to handle direct quantity input
    const handleUpdateQuantity = (cartLineId: string | undefined, newQuantity: number) => {
        if (!cartLineId || !newQuantity) {
            return;
        }

        cart.result?.updateCartLines({
            updatedCartLinesObject: [
                {
                    LineId: cartLineId,
                    Quantity: newQuantity
                }
            ]
        });
    };
    const handleIncreaseQuantity = (cartLineId: string | undefined, currentQuantity: number | undefined) => {
        if (!cartLineId || !currentQuantity) {
            return;
        }
        cart.result?.updateCartLines({
            updatedCartLinesObject: [
                {
                    LineId: cartLineId,
                    Quantity: currentQuantity + 1
                }
            ]
        });
    };

    const handleDecreaseQuantity = (cartLineId: string | undefined, currentQuantity: number | undefined) => {
        if (!cartLineId || !currentQuantity) {
            return;
        }
        if (currentQuantity === 1) {
            return;
        }
        cart.result?.updateCartLines({
            updatedCartLinesObject: [
                {
                    LineId: cartLineId,
                    Quantity: currentQuantity - 1
                }
            ]
        });
    };

    const iContext: IContext = {
        callerContext: props.context.actionContext,
        queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(props.context.actionContext)
    };
    const productId: number[] = [];
    if (cartlines && cartlines.length > 0) {
        cartlines.map(cartline => {
            productId.push(cartline.data?.cartline.ProductId as number);
        });
    }

    const [deliveryOptions, setDeliveryOptions] = React.useState<{
        isLoading: boolean;
        result: DeliveryOption[];
        error: Error | null;
    }>({
        isLoading: false,
        result: [],
        error: null
    });

    const getDeliveryOptions = async () => {
        if (Msdyn365.msdyn365Commerce.isBrowser) {
            const form = document.getElementById('deliveyOptionsForm') as HTMLFormElement;
            const formData = new FormData(form);
            // convert to json
            const object: any = {};
            formData.forEach((value, key) => {
                object[key] = value;
            });

            const address: Address = {
                ThreeLetterISORegionName: object.ThreeLetterISORegionName,
                County: object.county,
                ZipCode: object.zipCode
            };
            const cartId = cart.result?.cart.Id;
            if (cartId) {
                try {
                    setDeliveryOptions({
                        isLoading: true,
                        result: [],
                        error: null
                    });
                    const result = await getDeliveryOptionsAsync(iContext, cartId, address, [], null, true);
                    setDeliveryOptions({
                        isLoading: false,
                        result: result,
                        error: null
                    });
                } catch (e) {
                    setDeliveryOptions({
                        isLoading: false,
                        result: [],
                        error: e as Error
                    });
                }
            }
        }
    };

    const context = props.context.actionContext;

    const getProductPageUrl = (name: string | undefined, recordId: string | number | undefined) => {
        return getProductPageUrlSync(name as string, recordId as number, context);
    };

    const getConfigurations = (xml: string) => {
        // INSSELECTEDCONFIGURATION
        let configuration = '';
        if (xml) {
            parseString(xml, (err: Error | null, result: SelectedConfiguration) => {
                if (err) {
                    props.context.telemetry.log(LogLevel.Error, err.message);

                    return;
                }

                if (result && result.Session && result.Session.Attribute && result.Session.Attribute.length > 0) {
                    result.Session.Attribute.map(com => {
                        // configuration += `${com.$.name}-`;

                        configuration += `${com.$.readableValue}-`;
                    });
                }
            });
        }

        return configuration.slice(0, -1);
    };

    return (
        <div className='ml-cart-container'>
            <div className='ml-cart-title'>
                <p>
                    {resources.shoppingCartHeading} ({cart.result?.cart.CartLines?.length})
                </p>
            </div>
            <div className='ml-cart-wrapper'>
                <div className='ml-cart-main'>
                    {cart.result?.cart.CartLines?.length === 0 ? (
                        <div>There are no products in cart.</div>
                    ) : (
                        <>
                            <div className='ml-cart-subtitle'>
                                <p>{resources.cartItemHeading}</p>
                            </div>
                            <div className='ml-cart-items'>
                                <div className=''>
                                    {/* For small screens */}
                                    <div className='ml-cart-list-res'>
                                        {cartlines &&
                                            cartlines.map(product => (
                                                <div className='ml-cart-list-res-item' key={product.data?.cartline.LineId}>
                                                    {/* Product Image and Title */}
                                                    {/* <a
                                                        href={getProductPageUrl(
                                                            product.data?.product?.Name,
                                                            product.data?.product?.RecordId
                                                        )}
                                                    ></a> */}
                                                    {/* <a href=''> */}
                                                        <div className='ml-cart-list-res-item-1'>
                                                            <div className='ml-cart-list-res-product'>
                                                                <div className='ml-cart-list-res-item-img'>
                                                                    <img
                                                                        src={
                                                                            (product.data?.cartline.ExtensionProperties?.find(
                                                                                prop => prop.Key === 'INSCONFIGURATIONIIMAGE'
                                                                            )?.Value?.StringValue as string) ||
                                                                            product.data?.product?.PrimaryImageUrl
                                                                        }
                                                                        alt={product.data?.product?.Name}
                                                                    />
                                                                </div>
                                                                <div className='ml-cart-list-res-item-content'>
                                                                    <div className='ml-cart-list-res-item-title'>
                                                                        <p>{product.data?.product?.Name}</p>
                                                                    </div>
                                                                    {product.data?.cartline.DiscountLines &&
                                                                        product.data?.cartline.DiscountLines.length > 0 &&
                                                                        product.data.cartline.DiscountLines.map(discount => {
                                                                            return (
                                                                                <div className='ml-cart-list-res-item-discount-item'>
                                                                                    <p>
                                                                                        {discount.OfferName}: {resources.savingsLabel}{' '}
                                                                                        {getPrice(discount.EffectiveAmount)} (
                                                                                        {discount.EffectivePercentage}%)
                                                                                    </p>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    <div className='ml-cart-list-item-configuration'>
                                                                        <p>
                                                                            {getConfigurations(
                                                                                product.data?.cartline.ExtensionProperties?.find(
                                                                                    prop => prop.Key === 'INSSELECTEDREADABLECONFIGURATION'
                                                                                )?.Value?.StringValue as string
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className='ml-cart-list-res-item-price-section'>
                                                                <p className='ml-cart-list-res-item-old-price'>
                                                                    <del> {getPrice(product.data?.cartline?.NetPrice)}</del>
                                                                </p>
                                                                <p className='ml-cart-list-res-item-price'>
                                                                    {getPrice(product.data?.cartline.Price)}
                                                                </p>
                                                                {product.data?.cartline.DiscountLines &&
                                                                product.data?.cartline.DiscountLines.length === 0 ? null : (
                                                                    <div className='ml-cart-list-res-item-discount'>
                                                                        <p>
                                                                            {resources.savingsLabel}{' '}
                                                                            {getPrice(product.data?.cartline?.DiscountAmount)}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Product Count and Delete Icon */}
                                                        <div className='ml-cart-list-res-item-2'>
                                                            <div className='ml-cart-list-res-item-count'>
                                                                <button
                                                                    className='ml-cart-list-res-item-prev'
                                                                    onClick={() =>
                                                                        handleDecreaseQuantity(
                                                                            product.cartlineId,
                                                                            product.data?.cartline.Quantity
                                                                        )
                                                                    }
                                                                >
                                                                    <span> - </span>
                                                                </button>
                                                                <div className='ml-cart-list-res-item-quantity'>
                                                                    <input
                                                                        type='number'
                                                                        min='0'
                                                                        max='1000'
                                                                        className='ml-cart-list-res-item-counter'
                                                                        value={product.data?.cartline.Quantity}
                                                                        onChange={e =>
                                                                            handleUpdateQuantity(
                                                                                product.cartlineId,
                                                                                parseInt((e.target as HTMLInputElement).value, 10)
                                                                            )
                                                                        }
                                                                        onKeyDown={e => {
                                                                            if (
                                                                                e.key === 'Backspace' &&
                                                                                (e.target as HTMLInputElement).value.length === 1
                                                                            ) {
                                                                                // If backspace is pressed and there is only one digit, set quantity to 1
                                                                                handleUpdateQuantity(product.cartlineId, 1);
                                                                            } else if (e.key === 'Backspace') {
                                                                                // Handle backspace by updating the quantity
                                                                                handleUpdateQuantity(
                                                                                    product.cartlineId,
                                                                                    parseInt(
                                                                                        ((e.target as HTMLInputElement).value || '').slice(
                                                                                            0,
                                                                                            -1
                                                                                        ),
                                                                                        10
                                                                                    )
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                                {/* <p className='ml-cart-list-res-item-counter'>
                                                                    {product.data?.cartline.Quantity}
                                                                </p> */}
                                                                <button
                                                                    className='ml-cart-list-res-item-next'
                                                                    onClick={() =>
                                                                        handleIncreaseQuantity(
                                                                            product.cartlineId,
                                                                            product.data?.cartline.Quantity
                                                                        )
                                                                    }
                                                                >
                                                                    <span> + </span>
                                                                </button>
                                                            </div>
                                                            <div>
                                                                <p className='ml-cart-list-res-item-del'>
                                                                    <button
                                                                        id='RemoveItem'
                                                                        name='RemoveItem'
                                                                        aria-label='Remove item from cart'
                                                                        className='CartItem-Delete'
                                                                        onClick={() => handleRemoveCartLine(product.cartlineId)}
                                                                    >
                                                                        <span className='CartItem-DeleteButtonText'>
                                                                            {resources.removeButtonLabel}
                                                                        </span>
                                                                    </button>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    {/* </a> */}
                                                </div>
                                            ))}
                                        {/* <div className='ml-cart-summary'>
                                            <div className='ml-cart-summary-title'>
                                                <h3>Summary</h3>
                                            </div>
                                            <div className='ml-cart-price'>
                                                <div className='ml-cart-subtotal'>
                                                    <p>Subtotal:</p>
                                                    <p>{getPrice(cart.result?.cart.SubtotalAmount)}</p>
                                                </div>
                                            </div>
                                            <div className='ml-cart-total-price'>
                                                <div className='ml-cart-subtotal'>
                                                    <p>Total </p>
                                                    <p>{getPrice(cart.result?.cart.TotalAmount)}</p>
                                                </div>
                                            </div>
                                            <div className='ml-cart-checkout'>
                                                <a href={props.app.routes.checkout.destinationUrl}>CHECKOUT</a>
                                            </div>
                                        </div> */}
                                    </div>
                                    {/* For big screens */}
                                    <div className='ml-cart-list'>
                                        {cartlines &&
                                            cartlines.map(product => (
                                                <div className='ml-cart-list-item' key={product.data?.cartline.LineId}>
                                                    {/* Product Image and Title */}
                                                    <div className='ml-cart-list-item-1'>
                                                        <a
                                                            href={getProductPageUrl(
                                                                product.data?.product?.Name,
                                                                product.data?.product?.RecordId
                                                            )}
                                                        >
                                                            <div className='ml-cart-list-item-img'>
                                                                <img
                                                                    src={
                                                                        (product.data?.cartline.ExtensionProperties?.find(
                                                                            prop => prop.Key === 'INSCONFIGURATIONIIMAGE'
                                                                        )?.Value?.StringValue as string) ||
                                                                        product.data?.product?.PrimaryImageUrl
                                                                    }
                                                                    alt={product.data?.product?.Name}
                                                                />
                                                            </div>
                                                            <div className='ml-cart-list-content'>
                                                                <div className='ml-cart-list-item-title'>
                                                                    <p>{product.data?.product?.Name}</p>
                                                                </div>
                                                                {product.data?.cartline.DiscountLines &&
                                                                    product.data?.cartline.DiscountLines.length > 0 &&
                                                                    product.data.cartline.DiscountLines.map(discount => {
                                                                        return (
                                                                            <div className='ml-cart-list-item-discount'>
                                                                                <p>
                                                                                    {discount.OfferName}: {resources.savingsLabel}{' '}
                                                                                    {getPrice(discount.EffectiveAmount)} (
                                                                                    {discount.EffectivePercentage}%)
                                                                                </p>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                <div className='ml-cart-list-item-configuration'>
                                                                    <p>
                                                                        {getConfigurations(
                                                                            product.data?.cartline.ExtensionProperties?.find(
                                                                                prop => prop.Key === 'INSSELECTEDREADABLECONFIGURATION'
                                                                            )?.Value?.StringValue as string
                                                                        )}
                                                                    </p>
                                                                </div>
                                                                <div className='ml-cart-list-item-backorder'>
                                                                    <p>
                                                                        {
                                                                            product.data?.cartline.ExtensionProperties?.find(
                                                                                prop => prop.Key === 'INSBACKORDERBUTTONWARNING'
                                                                            )?.Value?.StringValue
                                                                        }
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </div>

                                                    {/* Product Count and Remove Icon */}
                                                    <div className='ml-cart-list-item-2'>
                                                        <div className='ml-cart-list-item-count'>
                                                            <button
                                                                className='ml-cart-list-item-prev'
                                                                onClick={() =>
                                                                    handleDecreaseQuantity(
                                                                        product.cartlineId,
                                                                        product.data?.cartline.Quantity
                                                                    )
                                                                }
                                                            >
                                                                <span> - </span>
                                                            </button>
                                                            {/*                                                           
                                                            <input
                                                                type='number'
                                                                min="0"
                                                                max="1000"
                                                                className='ml-cart-list-item-counter'
                                                                value={product.data?.cartline.Quantity}
                                                                onChange={(e) =>
                                                                    handleUpdateQuantity(
                                                                        product.cartlineId,
                                                                        parseInt(e.target.value, 10)
                                                                    )
                                                                }
                                                            /> */}
                                                            <div className='ml-cart-list-item-quantity'>
                                                                <input
                                                                    type='number'
                                                                    min='0'
                                                                    max='1000'
                                                                    className='ml-cart-list-item-counter'
                                                                    value={product.data?.cartline.Quantity}
                                                                    onChange={e =>
                                                                        handleUpdateQuantity(
                                                                            product.cartlineId,
                                                                            parseInt((e.target as HTMLInputElement).value, 10)
                                                                        )
                                                                    }
                                                                    onKeyDown={e => {
                                                                        if (
                                                                            e.key === 'Backspace' &&
                                                                            (e.target as HTMLInputElement).value.length === 1
                                                                        ) {
                                                                            // If backspace is pressed and there is only one digit, set quantity to 1
                                                                            handleUpdateQuantity(product.cartlineId, 1);
                                                                        } else if (e.key === 'Backspace') {
                                                                            // Handle backspace by updating the quantity
                                                                            handleUpdateQuantity(
                                                                                product.cartlineId,
                                                                                parseInt(
                                                                                    ((e.target as HTMLInputElement).value || '').slice(
                                                                                        0,
                                                                                        -1
                                                                                    ),
                                                                                    10
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                    onBlur={e => {
                                                                        // Handle blur by updating the quantity
                                                                        handleUpdateQuantity(
                                                                            product.cartlineId,
                                                                            parseInt((e.target as HTMLInputElement).value, 10)
                                                                        );
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* <p className='ml-cart-list-item-counter'>{product.data?.cartline.Quantity}</p> */}
                                                            <button
                                                                className='ml-cart-list-item-next'
                                                                onClick={() =>
                                                                    handleIncreaseQuantity(
                                                                        product.cartlineId,
                                                                        product.data?.cartline.Quantity
                                                                    )
                                                                }
                                                            >
                                                                <span> + </span>
                                                            </button>
                                                        </div>
                                                        <p className='ml-cart-list-item-del'>
                                                            <button
                                                                id='RemoveItem'
                                                                name='RemoveItem'
                                                                aria-label='Remove item from cart'
                                                                className='CartItem-Delete'
                                                                onClick={() => handleRemoveCartLine(product.cartlineId)}
                                                            >
                                                                <span className='CartItem-DeleteButtonText'>
                                                                    {resources.removeButtonLabel}
                                                                </span>
                                                            </button>
                                                        </p>
                                                    </div>

                                                    {/* Product Price */}

                                                    <div className='ml-cart-list-item-3 ml-cart-list-res-item-price-section'>
                                                        {product.data?.cartline.DiscountLines &&
                                                        product.data?.cartline.DiscountLines.length === 0 ? null : (
                                                            <p className='ml-cart-list-item-old-price'>
                                                                <del> {getPrice(product.data?.cartline?.NetPrice)}</del>
                                                            </p>
                                                        )}
                                                        <p className='ml-cart-list-item-price'>
                                                            {getPrice(product.data?.cartline?.TotalAmount)}
                                                        </p>
                                                        {product.data?.cartline.DiscountLines &&
                                                        product.data?.cartline.DiscountLines.length === 0 ? null : (
                                                            <div className='ml-cart-list-item-discount'>
                                                                <p>
                                                                    {resources.savingsLabel}{' '}
                                                                    {getPrice(product.data?.cartline?.DiscountAmount)}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                   
                    <div className='ml-cart-coupon'>
                        <p className='ml-cart-coupon-text'>{resources.couponLabel}</p>
                        <div className='ml-cart-coupon-field'>
                            <input type='text' value='' placeholder={resources.couponInputPlaceholder} />
                            <button type='submit' className={`${cart.result?.cart.CartLines?.length === 0 ? 'ml-disabled' : ''}`}>
                            {resources.couponApplyButtonLabel}
                            </button>
                        </div>
                    </div>
                </div>

                <div className='ml-cart-sidebar'>
                    <div className='ml-cart-summary'>
                        <div className='ml-cart-summary-title'>
                            <h3>{resources.orderSummaryTitle}</h3>
                        </div>
                        <div className='ml-cart-price'>
                            <div className='ml-cart-subtotal'>
                                <p>{resources.subtotalLabel}:</p>
                                <p>{getPrice(cart.result?.cart.NetPrice)}</p>
                            </div>
                            <div className='ml-cart-subtotal'>
                                <p>{resources.discountStringText}:</p>
                                <p>-{getPrice(cart.result?.cart.DiscountAmount)}</p>
                            </div>
                            <p className='tax-label-text'>{config.taxLabel}</p>
                        </div>
                        <div className='ml-cart-total-price'>
                            <div className='ml-cart-subtotal'>
                                <p>Total </p>
                                <p>{getPrice(cart.result?.cart.TotalAmount)}</p>
                            </div>
                        </div>
                        {cart.result?.cart.TotalAmount &&
                        cart.result?.cart.TotalAmount >= parseFloat(config.freeDeliveryLimit?.toString() as string) ? (
                            <p className='free-delivery-text'>{config.freeDeliveryText}</p>
                        ) : null}
                        <div className='ml-cart-checkout'>
                            {user.isAuthenticated ? (
                                <a
                                    href={props.app.routes.checkout.destinationUrl}
                                    className={`${cart.result?.cart.CartLines?.length === 0 ? 'ml-disabled' : ''}`}
                                >
                                    CHECKOUT
                                </a>
                            ) : (
                                <a href={user.signInUrl} className={`${cart.result?.cart.CartLines?.length === 0 ? 'ml-disabled' : ''}`}>
                                    CHECKOUT
                                </a>
                            )}
                        </div>

                        {props.config.showGuestCheckoutButton && !user.isAuthenticated && (
                            <div className={`ml-cart-checkout ${cart.result?.cart.CartLines?.length === 0 ? 'ml-disabled' : ''}`}>
                                <a href={props.app.routes.checkout.destinationUrl}>GUEST CHECKOUT</a>
                            </div>
                        )}
                    </div>
                    <div className='ml-cart-delivery-msg'>
                        <p>{<Msdyn365.RichTextComponent text={config.freeDeliveryPromotion} />}</p>
                    </div>
                    <div className='ml-cart-estimate'>
                        <h3 className='ml-cart-estimate-title'>Estimate Shipping</h3>
                        <p className='ml-cart-estimate-desc'>Enter your destination to get a shipping estimate</p>
                        <div className='ml-cart-estimate-form'>
                            <form id='deliveyOptionsForm' action=''>
                                <div>
                                    <label>Country:</label>
                                    <br />

                                    <select name='ThreeLetterISORegionName'>
                                        <option value=''>Select Country</option>
                                        {countryRegions.result &&
                                            countryRegions.result.length > 0 &&
                                            countryRegions.result.map(country => {
                                                return (
                                                    <option key={country.CountryRegionId} value={country.CountryRegionId}>
                                                        {country.ShortName}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </div>
                                <div>
                                    <label>County:</label>
                                    <br />
                                    <input type='text' name='county' />
                                </div>
                                <div>
                                    <label>Zip/Postcode:</label>
                                    <br />
                                    <input type='text' name='zipCode' />
                                </div>
                            </form>

                            <div className='ml-cart-res-shipping'>
                                <button onClick={getDeliveryOptions}>CHECK SHIPPING CHARGE</button>
                            </div>
                            <p className='ml-cart-estimate-results'>{deliveryOptions.isLoading ? 'Loading...' : ''}</p>

                            <p className='ml-cart-estimate-results'>
                                {deliveryOptions &&
                                    deliveryOptions.result &&
                                    deliveryOptions.result.map(option => {
                                        return (
                                            <p key={option.Code}>
                                                {option.Description} - {getPrice(option.TotalChargeAmount)}
                                            </p>
                                        );
                                    })}
                            </p>
                            <p className='ml-cart-estimate-results'>
                                {deliveryOptions.error && <Msdyn365.RichTextComponent text={config.deliveryNotAvailableText} />}
                            </p>

                            <div className='ml-cart-res-checkout'>
                                <a href={props.app.routes.checkout.destinationUrl}>CHECKOUT</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='ml-cart-faq'>
                <p>
                    For any questions check our FAQ's or <a href='#'>Contact Us</a>
                </p>
            </div>
        </div>
    );
};

export default CartView;
